import React from "react";
// Import the family tree library as a fallback
import FamilyTreeLib from '@balkangraph/familytree.js';
import { useGenealogyData } from '../../hooks/useGenealogyData';
import { transformToBalkanFormatWithGenerations, buildTreeMetadata } from '../../utils/genealogyUtils';
import { useState } from 'react';
import InviteFamilyModal from './InviteFamilyModal';
import { useFamily } from '../../contexts/FamilyContext';
import FamilyTreeBuilder from '../family/FamilyTreeBuilder';
import FamilyTreeViewer from '../family/FamilyTreeViewer';
import '../family/FamilyTree.css';

export default function BalkanFamilyTree({ embedded = false }) {
  const ref = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [treeError, setTreeError] = useState(null);
  const [isSinglePerson, setIsSinglePerson] = useState(false);
  
  // Modal state for invite family member
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [selectedRelationshipType, setSelectedRelationshipType] = useState(null);
  const [selectedParentInfo, setSelectedParentInfo] = useState(null); // For dual parent relationships

  // Get real family ID from context
  const { activeFamilyId } = useFamily();

  // Store balkanNodes for reference
  const [currentBalkanNodes, setCurrentBalkanNodes] = useState([]);

  // State for anchor person name in modal
  const [anchorPersonName, setAnchorPersonName] = useState('');

  // State for person details modal
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [detailsPersonData, setDetailsPersonData] = useState(null);
  
  // Use the genealogy data hook
  const { 
    familyTree, 
    allPersons, 
    rootPerson, 
    loading, 
    error, 
    refreshData,
    changeRootPerson,
    loadMoreGenerations 
  } = useGenealogyData();

  // Get options for the family tree configuration
  function getOptions() {
    const searchParams = new URLSearchParams(window.location.search);
    var fit = searchParams.get('fit');
    var enableSearch = true;
    var scaleInitial = 1;
    if (fit === 'yes') {
      enableSearch = false;
      scaleInitial = window.FamilyTree.match.boundary;
    }
    return { enableSearch, scaleInitial };
  }

  // Open invite modal with pre-selected relationship type
  const openInviteModal = (personId, relationshipType) => {
    setSelectedPersonId(personId);
    setSelectedRelationshipType(relationshipType);
    setSelectedParentInfo(null); // Reset parent info for single parent relationships
    setIsInviteModalOpen(true);
  };

  // Open invite modal for child relationships with both parents
  const openInviteModalWithParents = (relationshipType, parentInfo) => {
    setSelectedPersonId(null); // No single person for dual parent relationships
    setSelectedRelationshipType(relationshipType);
    setSelectedParentInfo(parentInfo);
    setIsInviteModalOpen(true);
  };

  // Close invite modal and reset state
  const closeInviteModal = () => {
    setIsInviteModalOpen(false);
    setSelectedPersonId(null);
    setSelectedRelationshipType(null);
    setSelectedParentInfo(null);
  };

  // Handle successful invitation submission
  const handleInvitationSuccess = async () => {
    // Refresh the family tree data
    await refreshData();
    closeInviteModal();
  };

  // Load Balkan FamilyTree library directly from imported package
  React.useEffect(() => {
    const initializeTree = async () => {
      try {
        setTreeError(null);
        
        // Use the imported package directly
        if (FamilyTreeLib) {
          window.FamilyTree = FamilyTreeLib;
          setReady(true);
        } else {
          throw new Error('FamilyTree library not available');
        }
      } catch (e) {
        console.error('Failed to load Balkan FamilyTree', e);
        setTreeError('Failed to load family tree library. Please check that the package is properly installed.');
      }
    };

    initializeTree();
  }, []);

  // Initialize the family tree
  React.useEffect(() => {
    if (!ready || !ref.current || !window.FamilyTree || !familyTree) return;

    // Additional safety check to ensure DOM element is properly mounted
    if (!ref.current || !ref.current.offsetParent) {
      return;
    }

    // Add a small delay to ensure DOM is fully ready
    const initTimer = setTimeout(() => {
      try {
        // Clear previous content
        ref.current.innerHTML = '';

        // Get options for the family tree
        const options = getOptions();

        // Debug: Log the actual API response
        
        // Process API data using the specified pattern
        let balkanNodes;
        let treeData;
        try {
          // 1. Process the data for BalkanTree using the legacy working function
          balkanNodes = transformToBalkanFormatWithGenerations(familyTree);
          
          if (!balkanNodes || balkanNodes.length === 0) {
            throw new Error('No valid family tree data to display');
          }

          // Store balkanNodes in state for use by context menu
          setCurrentBalkanNodes(balkanNodes);

          // 2. Store processed data for reference
          treeData = {
            nodes: balkanNodes,
            generationMap: familyTree.generation_map,
            familyMembers: familyTree.family_members,
            rootPerson: {
              id: familyTree.id,
              name: familyTree.name
            }
          };
          
          // Check if Anil Patel is included
          const anilPatel = familyTree.family_members?.find(member =>
            member.name?.includes('Anil Patel')
          );

          if (anilPatel) {
          } else {
          }

          // Log generation map
          
          // SONALI DEBUG - Check if she's in the processed nodes
          const sonaliNode = balkanNodes.find(node => node.name?.includes('Sonali') || node.id === '1afb8b12-8bfa-4535-a60f-b78e5ebceb7f');
          if (sonaliNode) {
          } else {
          }
          
        } catch (transformError) {
          console.error('Error processing family tree data:', transformError);
          throw new Error(`Failed to process family tree data: ${transformError.message}`);
        }
        
        // const treeMetadata = buildTreeMetadata(familyTree); // Unused for now

        // Clear any existing tree content
        ref.current.innerHTML = '';
        
        // 🔍 BALKAN TREE DEBUG - Log what we're passing to BalkanTree
        balkanNodes.forEach((node, index) => {
        });
        
        // Nodes are pre-sorted by generation (oldest first) in transformToBalkanFormatWithGenerations.
        // Let BalkanTree auto-detect roots — it will pick the first root node in the array
        // which is now the oldest generation (e.g. Joseph instead of Nitin).
        const peopleWithNoParents = balkanNodes.filter(node => !node.mid && !node.fid);

        // Debug: Check what nodeMenuButton looks like on hugo template

        // Customize ALL hugo template variants — remove top-left icon from nodes
        ['hugo', 'hugo_male', 'hugo_female'].forEach(name => {
          if (window.FamilyTree.templates[name]) {
            window.FamilyTree.templates[name].nodeTreeMenuButton = '';
            window.FamilyTree.templates[name].nodeTreeMenuCloseButton = '';
          }
        });

        // Also remove from base template (inherited by all)
        if (window.FamilyTree.templates.base) {
          window.FamilyTree.templates.base.nodeTreeMenuButton = '';
          window.FamilyTree.templates.base.nodeTreeMenuCloseButton = '';
        }

        // Hide ghost "add relative" placeholder nodes
        const ghostTemplates = ['mother', 'father', 'partner', 'son', 'daughter', 'husband', 'wife'];
        ghostTemplates.forEach(name => {
          if (window.FamilyTree.templates[name]) {
            window.FamilyTree.templates[name].node = '<g></g>';
            window.FamilyTree.templates[name].field_0 = '';
            window.FamilyTree.templates[name].field_1 = '';
            window.FamilyTree.templates[name].img_0 = '';
            window.FamilyTree.templates[name].nodeMenuButton = '';
            window.FamilyTree.templates[name].nodeTreeMenuButton = '';
            window.FamilyTree.templates[name].nodeTreeMenuCloseButton = '';
            window.FamilyTree.templates[name].size = [0, 0];
          }
        });

        // 3. Initialize BalkanTree with processed nodes
        const family = new window.FamilyTree(ref.current, {
          nodes: balkanNodes,

          // BalkanTree configuration
          mouseScrool: window.FamilyTree.none,
          scaleInitial: window.FamilyTree.match.boundary,
          mode: 'dark',
          template: 'hugo',
          orientation: window.FamilyTree.orientation.top,

          // Customize node appearance
          nodeBinding: {
            field_0: 'name',
            field_1: 'born',
            img_0: 'photo'
          },

          // Custom three-dots menu with onClick handlers
          nodeMenu: {
            details: {
              text: 'Details',
              onClick: function(nodeId) {
                const nodeData = balkanNodes.find(node => node.id === nodeId);
                if (nodeData) {
                  setDetailsPersonData(nodeData);
                  setIsDetailsModalOpen(true);
                }
              }
            },
            invite: {
              text: 'Invite Family',
              onClick: function(nodeId) {
                const nodeData = balkanNodes.find(node => node.id === nodeId);
                if (nodeData) {
                  setAnchorPersonName(nodeData.name);
                  openInviteModal(nodeData.id, null);
                }
              }
            },
          },
          editForm: false,
          nodeTreeMenu: false,
          enableSearch: false,
          enableDragDrop: false,
          lazyLoading: false,

          // Toolbar: zoom/fit only
          toolbar: {
            layout: window.FamilyTree.normal,
            zoom: true,
            fit: true,
            expandAll: false
          },
        });

        // Add event handler for date formatting
        family.on('field', function (sender, args) {
          if (args.name === 'born') {
            // Only process if the value looks like it should be formatted as a date
            // Skip if it already contains "DOB:" prefix or is null/empty
            if (args.value && !args.value.includes('DOB:') && args.value.trim() !== '') {
              // Parse date string WITHOUT timezone interpretation
              // "2010-07-29" should be July 29, not shifted by timezone
              var parts = args.value.split('-');
              if (parts.length === 3) {
                var year = parseInt(parts[0], 10);
                var month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
                var day = parseInt(parts[2], 10);
                var date = new Date(year, month, day); // Creates local date, not UTC
                if (!isNaN(date.getTime())) {
                  args.value = date.toLocaleDateString();
                }
              }
            }
          }
        });

        // Prevent default edit form from opening on any node interaction
        family.on('click', function(sender, args) {
          return false; // Prevent default click behavior (edit form)
        });

        // Note: nodes are already loaded via the config, no need to call family.load()

        // Log generation mapping info
        if (treeData.generationMap) {
          const totalPeople = Object.keys(treeData.generationMap).length;
        } else {
        }
        
        // If we only have one person, show a message
        if (balkanNodes.length === 1) {
          setIsSinglePerson(true);
        } else if (balkanNodes.length > 1) {
          setIsSinglePerson(false);
        } else {
          setIsSinglePerson(true);
        }
        
        // Store treeData for debug access (using a ref to avoid re-renders)
        window.familyTreeDebugData = treeData;


      } catch (error) {
        console.error('Error initializing Balkan FamilyTree:', error);
        setTreeError('Error rendering family tree. Please try refreshing the page.');
      }
    }, 100); // 100ms delay to ensure DOM is ready

    // Cleanup timer on unmount
    return () => clearTimeout(initTimer);

  }, [ready, familyTree, rootPerson]);

  // Show API error if any
  if (error) {
    return (
      <div className="w-full min-h-screen bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-900 border border-red-700 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.34 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-red-200 mb-3">Unable to Load Family Tree</h3>
            <p className="text-red-300 mb-4">{error}</p>
            <div className="bg-gray-800 rounded-lg p-4 text-left">
              <p className="text-sm text-red-200 font-medium mb-2">Troubleshooting:</p>
              <ul className="text-xs text-red-300 space-y-1">
                <li>• Check your internet connection</li>
                <li>• Verify the API server is running</li>
                <li>• Check browser console for detailed errors</li>
                <li>• Try refreshing the page</li>
              </ul>
            </div>
            <button 
              onClick={refreshData}
              className="mt-4 bg-red-700 hover:bg-red-600 text-white px-6 py-2 rounded-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show tree library error if any
  if (treeError) {
    return (
      <div className="w-full min-h-screen bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-900 border border-red-700 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.34 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-red-200 mb-3">Unable to Load Family Tree</h3>
            <p className="text-red-300 mb-4">{treeError}</p>
            <div className="bg-gray-800 rounded-lg p-4 text-left">
              <p className="text-sm text-red-200 font-medium mb-2">Troubleshooting:</p>
              <ul className="text-xs text-red-300 space-y-1">
                <li>• Check that @balkangraph/familytree.js is installed</li>
                <li>• Verify the package is properly imported</li>
                <li>• Check browser console for detailed errors</li>
                <li>• Try running: npm install @balkangraph/familytree.js</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading || !ready) {
    return (
      <div className={`w-full ${embedded ? 'min-h-[500px]' : 'min-h-screen'} ${embedded ? 'bg-gray-100' : 'bg-gray-900'} p-6 flex items-center justify-center`}>
        <div className="text-center">
          <div className={`w-16 h-16 ${embedded ? 'bg-[#556B2F]' : 'bg-blue-800'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <div className={`animate-spin w-8 h-8 border-3 ${embedded ? 'border-white' : 'border-blue-400'} border-t-transparent rounded-full`}></div>
          </div>
          <h3 className={`text-xl font-semibold ${embedded ? 'text-gray-800' : 'text-white'} mb-2`}>
            {loading ? 'Loading Family Tree...' : 'Initializing Tree View'}
          </h3>
          <p className={embedded ? 'text-gray-600' : 'text-gray-300'}>
            {loading ? 'Fetching genealogy data...' : 'Setting up visualization...'}
          </p>
        </div>
      </div>
    );
  }

  // Show no data state
  if (!familyTree || !rootPerson) {
    return (
      <div className={`w-full ${embedded ? 'min-h-[500px]' : 'min-h-screen'} ${embedded ? 'bg-gray-100' : 'bg-gray-900'} p-6 flex items-center justify-center`}>
        <div className="text-center">
          <div className={`w-16 h-16 ${embedded ? 'bg-gray-300' : 'bg-blue-800'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <svg className={`w-8 h-8 ${embedded ? 'text-gray-500' : 'text-blue-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
            </svg>
          </div>
          <h3 className={`text-xl font-semibold ${embedded ? 'text-gray-800' : 'text-white'} mb-3`}>No Family Tree Data</h3>
          <p className={`${embedded ? 'text-gray-600' : 'text-gray-300'} mb-4`}>No family tree data found. Please add family members to get started.</p>
          <button
            onClick={refreshData}
            className={`${embedded ? 'bg-[#556B2F] hover:bg-[#4A5D23]' : 'bg-blue-700 hover:bg-blue-600'} text-white px-6 py-2 rounded-lg`}
          >
            Refresh Data
          </button>
        </div>
      </div>
    );
  }

  // Debug function to inspect data
  const debugTreeData = () => {
  };

  // Build metadata for display
  const treeMetadata = buildTreeMetadata(familyTree);
  const totalPersons = familyTree?.family_members?.length || treeMetadata?.totalPersons || 0;
  const totalGenerations = Object.keys(familyTree?.generation_map || {}).length || treeMetadata?.totalGenerations || 0;
  
  // Get the root person name from the data
  let rootPersonName = 'Family Tree';
  if (familyTree?.name) {
    rootPersonName = familyTree.name;
  } else if (window.familyTreeDebugData?.rootPerson?.name) {
    rootPersonName = window.familyTreeDebugData.rootPerson.name;
  } else if (rootPerson) {
    rootPersonName = `${rootPerson.first_name || ''} ${rootPerson.last_name || ''}`.trim();
  }

  return (
    <div className={`w-full ${embedded ? 'min-h-[600px]' : 'min-h-screen'} ${embedded ? 'bg-white' : 'bg-gray-900'}`}>
      {/* Header - only show when not embedded */}
      {!embedded && (
      <div className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">{rootPersonName} Family Tree</h1>
              <p className="text-gray-300 mt-1">Interactive genealogy with /genealogy/people/balkan_tree/ API data</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-400">
                <span className="font-medium text-white">{totalPersons}</span> family members
              </div>
              <div className="text-sm text-gray-400">
                <span className="font-medium text-white">{totalGenerations}</span> generations
              </div>
              <button
                onClick={() => {
                  openInviteModal('test-person', '39'); // Test with Mother relationship
                }}
                className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg text-sm"
              >
                Test Modal
              </button>
              <button
                onClick={debugTreeData}
                className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-sm"
              >
                Debug Data
              </button>
              <button
                onClick={refreshData}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Family Tree Builder - only show when not embedded */}
      {!embedded && (
      <div className="p-6">
        <div className="max-w-7xl mx-auto mb-6">
          <FamilyTreeBuilder
            currentUserId={rootPerson?.id}
            familyMembers={familyTree?.family_members || []}
            onFamilyUpdate={handleInvitationSuccess}
          />
        </div>
      </div>
      )}

      {/* Family Tree Data Viewer - only show when not embedded */}
      {!embedded && (
      <div className="p-6">
        <div className="max-w-7xl mx-auto mb-6">
          <FamilyTreeViewer apiResponse={familyTree} />
        </div>
      </div>
      )}

      {/* Family Tree Container */}
      <div className={embedded ? 'p-2' : 'p-6'}>
        <div className="max-w-full mx-auto">
          <div
            className={`${embedded ? 'bg-white' : 'bg-gray-800'} rounded-xl shadow-lg ${embedded ? 'border-0' : 'border border-gray-700'} overflow-hidden`}
            style={{height: embedded ? '580px' : '800px'}}
          >
            <div ref={ref} className="w-full h-full" />
          </div>
        </div>
        
        {/* Single Person Message */}
        {isSinglePerson && (
          <div className="mt-6 max-w-4xl mx-auto">
            <div className="bg-blue-900 border border-blue-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-blue-200 mb-3">Single Person View</h3>
              <p className="text-blue-300 mb-4">
                Currently showing only one person. To build a family tree, you'll need to add family members and relationships.
              </p>
              <div className="bg-blue-800 rounded-lg p-4 text-left">
                <p className="text-sm text-blue-200 font-medium mb-2">Next Steps:</p>
                <ul className="text-xs text-blue-300 space-y-1">
                  <li>• Use the Family Hub to invite family members</li>
                  <li>• Add relationships between family members</li>
                  <li>• The tree will automatically expand as you add more people</li>
                </ul>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Person Details Modal */}
      {isDetailsModalOpen && detailsPersonData && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsDetailsModalOpen(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setIsDetailsModalOpen(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-[#556B2F] to-[#6B8E23] text-white px-6 py-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">{detailsPersonData.name}</h3>
                  <button onClick={() => setIsDetailsModalOpen(false)} className="text-white/80 hover:text-white">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Details */}
              <div className="p-6 space-y-4">
                {detailsPersonData.photo && !detailsPersonData.photo.includes('default') && (
                  <div className="flex justify-center">
                    <img src={detailsPersonData.photo} alt={detailsPersonData.name} className="w-24 h-24 rounded-full object-cover border-4 border-[#6B8E23]" />
                  </div>
                )}
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Full Name</label>
                    <p className="text-lg text-gray-900">{detailsPersonData.name}</p>
                  </div>
                  {detailsPersonData.born && (
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Date of Birth</label>
                      <p className="text-lg text-gray-900">{detailsPersonData.born}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Gender</label>
                    <p className="text-lg text-gray-900 capitalize">{detailsPersonData.gender || 'Not specified'}</p>
                  </div>
                  {detailsPersonData.email && (
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</label>
                      <p className="text-lg text-gray-900">{detailsPersonData.email}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Invite Family Modal */}
      <InviteFamilyModal
        isOpen={isInviteModalOpen}
        onClose={closeInviteModal}
        familyId={activeFamilyId}
        preSelectedRelationshipType={selectedRelationshipType}
        selectedPersonId={selectedPersonId}
        selectedParentInfo={selectedParentInfo}
        rootPersonId={rootPerson?.id}
        onSuccess={handleInvitationSuccess}
        anchorPersonName={anchorPersonName}
      />
    </div>
  );
}
